import React from "react";
import { Box, Grid, Stack, TextField, Accordion, AccordionSummary, AccordionDetails, Typography, 
  ToggleButtonGroup, ToggleButton, Button, FormControl, Select, MenuItem, } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CustomTextField = ({ label, name, value, onChange }) => (
  <TextField
    label={label}
    name={name}
    value={value}
    onChange={onChange}
    style={{ minWidth: "26%" }}
  />
);

const ArrangementField = ({ label, value, onChange }) => (
  <Grid item xs={12} style={{ paddingBottom: "5%" }}>
    <Stack direction="row" alignItems="center">
      <Typography variant="h6" style={{ minWidth: "35%" }}>
        {label}
      </Typography>
      <Box style={{ flexGrow: "1", minWidth: "60%" }}>
        <CustomTextField label={label} name={label} value={value} onChange={onChange} />
      </Box>
    </Stack>
  </Grid>
);

const DetectionZoneAccordion = ({
  zoneIndex,
  zone,
  onChange,
  onRemove,
  arrangementIndex,
  zoneLocationList,
  thermalDetectionList,
  flameDetectionList,
}) => (
  <Accordion>
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls={`detection-zone-${arrangementIndex}-${zoneIndex}-content`}
      id={`detection-zone-${arrangementIndex}-${zoneIndex}-header`}
    >
      <Typography>Detection Zone {zoneIndex + 1}</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Stack style={{ paddingTop: "2.5%" }} spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1">Enable</Typography>
          <ToggleButtonGroup
            color="primary"
            value={zone.enabled}
            exclusive
            onChange={(event, newValue) =>
              newValue !== null && onChange(zoneIndex, "enabled", newValue)
            }
            aria-label="Enable detection zone"
          >
            <ToggleButton color="success" value={true}>
              Yes
            </ToggleButton>
            <ToggleButton value={false}>No</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography>Location</Typography>
          <FormControl sx={{ width: "40%" }}>
            <Select
              labelId="zone-location-label"
              id="zone-location-select"
              value={zone.zoneLocation}
              onChange={(event) =>
                onChange(zoneIndex, "zoneLocation", event.target.value)
              }
            >
              {zoneLocationList.map((pm) => (
                <MenuItem key={pm.id} value={pm.name}>
                  {pm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography>Thermal Detection</Typography>
          <FormControl sx={{ width: "40%" }}>
            <Select
              labelId="thermal-detection-label"
              id="thermal-detection-select"
              value={zone.thermalDetection}
              onChange={(event) =>
                onChange(zoneIndex, "thermalDetection", event.target.value)
              }
            >
              {thermalDetectionList.map((pm) => (
                <MenuItem key={pm.id} value={pm.name}>
                  {pm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography>Flame Detection</Typography>
          <FormControl sx={{ width: "40%" }}>
            <Select
              labelId="flame-detection-label"
              id="flame-detection-select"
              value={zone.flameDetection}
              onChange={(event) =>
                onChange(zoneIndex, "flameDetection", event.target.value)
              }
            >
              {flameDetectionList.map((pm) => (
                <MenuItem key={pm.id} value={pm.name}>
                  {pm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1">Smoke Analytics</Typography>
          <ToggleButtonGroup
            color="primary"
            value={zone.smokeAnalytics}
            exclusive
            onChange={(event, newValue) =>
              newValue !== null && onChange(zoneIndex, "smokeAnalytics", newValue)
            }
            aria-label="Smoke Analytics"
          >
            <ToggleButton color="success" value={true}>
              Yes
            </ToggleButton>
            <ToggleButton value={false}>No</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1">
            Indicator Light (if no suppression)
          </Typography>
          <ToggleButtonGroup
            color="primary"
            value={zone.indicatorLight}
            exclusive
            onChange={(event, newValue) =>
              newValue !== null && onChange(zoneIndex, "indicatorLight", newValue)
            }
            aria-label="Indicator Light"
          >
            <ToggleButton color="success" value={true}>
              Yes
            </ToggleButton>
            <ToggleButton value={false}>No</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Button
          variant="contained"
          color="secondary"
          onClick={() => onRemove(zoneIndex)}
        >
          Remove Detection Zone
        </Button>
      </Stack>
    </AccordionDetails>
  </Accordion>
);

const SuppressionZoneAccordion = ({
  zoneIndex,
  zone,
  onChange,
  onRemove,
  arrangementIndex,
  suppressionMastTowerBuildingList,
  monitorNozzleConfigList,
  nozzleList,
}) => (
  <Accordion>
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls={`suppression-zone-${arrangementIndex}-${zoneIndex}-content`}
      id={`suppression-zone-${arrangementIndex}-${zoneIndex}-header`}
    >
      <Typography>Suppression Zone {zoneIndex + 1}</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Stack style={{ paddingTop: "2.5%" }} spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1">Enabled</Typography>
          <ToggleButtonGroup
            color="primary"
            value={zone.enabled}
            exclusive
            onChange={(event, newValue) =>
              newValue !== null && onChange(zoneIndex, "enabled", newValue)
            }
            aria-label="Enable suppression zone"
          >
            <ToggleButton color="success" value={true}>
              Yes
            </ToggleButton>
            <ToggleButton value={false}>No</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography>Mast/Tower/Building</Typography>
          <FormControl sx={{ width: "40%" }}>
            <Select
              labelId="suppression-mast-tower-building-label"
              id="suppression-mast-tower-building-select"
              value={zone.suppressionMastTowerBuilding}
              onChange={(event) =>
                onChange(zoneIndex, "suppressionMastTowerBuilding", event.target.value)
              }
            >
              {suppressionMastTowerBuildingList.map((pm) => (
                <MenuItem key={pm.id} value={pm.name}>
                  {pm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography>Monitor/Nozzle Config</Typography>
          <FormControl sx={{ width: "40%" }}>
            <Select
              labelId="suppression-monitor-nozzle-config-label"
              id="suppression-monitor-nozzle-config-select"
              value={zone.monitorNozzleConfig}
              onChange={(event) =>
                onChange(zoneIndex, "monitorNozzleConfig", event.target.value)
              }
            >
              {monitorNozzleConfigList.map((pm) => (
                <MenuItem key={pm.id} value={pm.name}>
                  {pm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography>Nozzle</Typography>
          <FormControl sx={{ width: "40%" }}>
            <Select
              labelId="nozzle-label"
              id="nozzle-select"
              value={zone.nozzle}
              onChange={(event) =>
                onChange(zoneIndex, "nozzle", event.target.value)
              }
            >
              {nozzleList.map((pm) => (
                <MenuItem key={pm.id} value={pm.name}>
                  {pm.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography>Connect Type</Typography>
          <CustomTextField
            label="Connect Type"
            name="connectType"
            value={zone.connectType || ""}
            onChange={(event) =>
              onChange(zoneIndex, "connectType", event.target.value)
            }
          />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1">Riser Indicator Light</Typography>
          <ToggleButtonGroup
            color="primary"
            value={zone.riserIndicatorLight}
            exclusive
            onChange={(event, newValue) =>
              newValue !== null && onChange(zoneIndex, "riserIndicatorLight", newValue)
            }
            aria-label="Riser Indicator Light"
          >
            <ToggleButton color="success" value={true}>
              Yes
            </ToggleButton>
            <ToggleButton value={false}>No</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1">FD Connect</Typography>
          <ToggleButtonGroup
            color="primary"
            value={zone.fdConnect}
            exclusive
            onChange={(event, newValue) =>
              newValue !== null && onChange(zoneIndex, "fdConnect", newValue)
            }
            aria-label="FD Connect"
          >
            <ToggleButton color="success" value={true}>
              Yes
            </ToggleButton>
            <ToggleButton value={false}>No</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Button
          variant="contained"
          color="secondary"
          onClick={() => onRemove(zoneIndex)}
        >
          Remove Suppression Zone
        </Button>
      </Stack>
    </AccordionDetails>
  </Accordion>
);

const ArrangementAccordion = ({
  index,
  arrangement,
  onChange,
  onRemove,
  onAddDetectionZone,
  onChangeDetectionZone,
  onRemoveDetectionZone,
  onAddSuppressionZone,
  onChangeSuppressionZone,
  onRemoveSuppressionZone,
  zoneLocationList,
  thermalDetectionList,
  flameDetectionList,
  suppressionMastTowerBuildingList,
  monitorNozzleConfigList,
  nozzleList,
  selectedUnit,
}) => (
  <Accordion>
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls={`panel${index}-content`}
      id={`panel${index}-header`}
    >
      <Typography component="span">Arrangement {index + 1}</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <ArrangementField
        label="Location"
        value={arrangement.location}
        onChange={(event) => onChange(index, "location", event.target.value)}
      />
      <ArrangementField
        label="Name"
        value={arrangement.name}
        onChange={(event) => onChange(index, "name", event.target.value)}
      />
      <ArrangementField
        label="Mounting Height (feet above floor)"
        value={arrangement.mountingHeight}
        onChange={(event) => onChange(index, "mountingHeight", event.target.value)}
      />

      <Grid item xs={12} style={{ paddingBottom: "5%" }}>
        <Stack direction="row" alignItems="center">
          <Typography variant="h6" style={{ minWidth: "35%" }}>
            Ebutton
          </Typography>
          <Box style={{ flexGrow: "1", minWidth: "60%" }}>
            <ToggleButtonGroup
              color="primary"
              value={arrangement.ebutton}
              exclusive
              onChange={(event, newValue) =>
                newValue !== null && onChange(index, "ebutton", newValue)
              }
              aria-label="Ebutton"
              sx={{ paddingRight: "10%" }}
            >
              <ToggleButton color="success" value={true}>
                Yes
              </ToggleButton>
              <ToggleButton value={false}>No</ToggleButton>
            </ToggleButtonGroup>
            {arrangement.ebutton && (
              <CustomTextField
                label="Ebutton Instructions"
                name="Ebutton Instructions"
                value={arrangement.ebuttonInstructions}
                onChange={(event) =>
                  onChange(index, "ebuttonInstructions", event.target.value)
                }
              />
            )}
          </Box>
        </Stack>
      </Grid>

      {arrangement.detectionZones &&
        arrangement.detectionZones.map((zone, zoneIndex) => (
          <DetectionZoneAccordion
            key={zoneIndex}
            zoneIndex={zoneIndex}
            zone={zone}
            onChange={(zIndex, field, value) =>
              onChangeDetectionZone(index, zIndex, field, value)
            }
            onRemove={(zIndex) => onRemoveDetectionZone(index, zIndex)}
            arrangementIndex={index}
            zoneLocationList={zoneLocationList}
            thermalDetectionList={thermalDetectionList}
            flameDetectionList={flameDetectionList}
          />
        ))}

      { selectedUnit.value !== "ED"
      ?
        <Button
          variant="contained"
          color="primary"
          onClick={() => onAddDetectionZone(index)}
          sx={{ mt: 2, marginRight: "2%" }}
        >
          Add Detection Zone
        </Button>
      : null
      }

      {arrangement.suppressionZones &&
        arrangement.suppressionZones.map((zone, zoneIndex) => (
          <SuppressionZoneAccordion
            key={zoneIndex}
            zoneIndex={zoneIndex}
            zone={zone}
            onChange={(zIndex, field, value) =>
              onChangeSuppressionZone(index, zIndex, field, value)
            }
            onRemove={(zIndex) => onRemoveSuppressionZone(index, zIndex)}
            arrangementIndex={index}
            suppressionMastTowerBuildingList={suppressionMastTowerBuildingList}
            monitorNozzleConfigList={monitorNozzleConfigList}
            nozzleList={nozzleList}
          />
        ))}

      { selectedUnit.value !== "ED"
      ?  
        <Button
          variant="contained"
          color="primary"
          onClick={() => onAddSuppressionZone(index)}
          sx={{ mt: 2 }}
        >
          Add Suppression Zone
        </Button>
      : null
      }

      <Button
        variant="contained"
        color="secondary"
        onClick={() => onRemove(index)}
        sx={{ mt: 2, ml: 2 }}
      >
        Remove Arrangement
      </Button>
    </AccordionDetails>
  </Accordion>
);

export default function Arrangements({
  arrangements,
  handleAddArrangement,
  handleRemoveArrangement,
  handleChange,
  handleAddDetectionZone,
  handleRemoveDetectionZone,
  handleChangeDetectionZone,
  handleAddSuppressionZone,
  handleRemoveSuppressionZone,
  handleChangeSuppressionZone,
  selectedUnit
}) {

  const zoneLocationList=[
    { id: 0, name: "Interior" },
    { id: 1, name: "Exterior" },
    { id: 2, name: "Other" },
  ];

  const thermalDetectionList=[
    { id: 0, name: "FLIR A50 - 95 Degree" },
    { id: 1, name: "FLIR A50 - 51 Degree" },
    { id: 2, name: "FLIR A50 - 29 Degree" },
    { id: 3, name: "FLIR A70 - 95 Degree" },
    { id: 4, name: "FLIR A70 - 51 Degree" },
    { id: 5, name: "FLIR A70 - 29 Degree" },
    { id: 6, name: "None" },
  ];

  const flameDetectionList=[
    { id: 0, name: "OFD - Low setting" },
    { id: 1, name: "OFD - Medium setting" },
    { id: 2, name: "OFD - High setting" },
    { id: 3, name: "OFD - Extreme setting" },
    { id: 4, name: "None" },
  ];

  const suppressionMastTowerBuildingList=[
    { id: 0, name: "Standard Mast" },
    { id: 1, name: "Custom Mast" },
    { id: 2, name: "30' Tower" },
    { id: 3, name: "Building Mounted" },
  ];

  const monitorNozzleConfigList=[
    { id: 0, name: "Sidewinder EXM2" },
    { id: 1, name: "Cobra 2.5" },
    { id: 2, name: "Cobra 3.0" },
    { id: 3, name: "Other" },
  ];

  const nozzleList=[
    { id: 0, name: "7/8'' Smoothbore" },
    { id: 1, name: "1'' Smoothbore" },
    { id: 2, name: "1 3/8'' Smoothbore" },
    { id: 3, name: "Variable Stream" },
  ];


  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handleAddArrangement}
        sx={{ mb: 2 }}
      >
        Add Arrangement
      </Button>
      {arrangements.map((arrangement, index) => (
        <ArrangementAccordion
          key={index}
          index={index}
          arrangement={arrangement}
          onChange={handleChange}
          onRemove={handleRemoveArrangement}
          onAddDetectionZone={handleAddDetectionZone}
          onChangeDetectionZone={handleChangeDetectionZone}
          onRemoveDetectionZone={handleRemoveDetectionZone}
          onAddSuppressionZone={handleAddSuppressionZone}
          onChangeSuppressionZone={handleChangeSuppressionZone}
          onRemoveSuppressionZone={handleRemoveSuppressionZone}
          zoneLocationList={zoneLocationList}
          thermalDetectionList={thermalDetectionList}
          flameDetectionList={flameDetectionList}
          suppressionMastTowerBuildingList={suppressionMastTowerBuildingList}
          monitorNozzleConfigList={monitorNozzleConfigList}
          nozzleList={nozzleList}
          selectedUnit={selectedUnit}
        />
      ))}
    </>
  );
}
